import React from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SendIcon from "@mui/icons-material/Send";
import ClearIcon from "@mui/icons-material/Clear";
import { useState } from "react";
import { myExpariencesService } from "../../services";
import { useSnackbar } from "notistack";

const PREFIX = "hdrmanagement";

const classes = {
  allHdrList: `${PREFIX}-allHdrList`,
  deleteIconSize: `${PREFIX}-deleteIconSize`,
  frameOptionsContainer: `${PREFIX}-frameOptionsContainer`,
  inputStyle: `${PREFIX}-inputStyle`,
  hdrModalContent: `${PREFIX}-hdrModalContent`,
  filenameStyle: `${PREFIX}-filenameStyle`,
  fileButtonsStyle: `${PREFIX}-fileButtonsStyle`,
  filenameContainer: `${PREFIX}-filenameContainer`,
  hdrNameStyle: `${PREFIX}-hdrNameStyle`,
  titleStyle: `${PREFIX}-titleStyle`,
  dividerStyle: `${PREFIX}-dividerStyle`,
};

const StyledDiv = styled("div")(({ theme }) => {
  return {
    [`& .${classes.allHdrList}`]: {
      borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
      padding: "0.5rem",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
      maxWidth: "840px",
      maxHeight: "600px",
      overflowY: "auto",
      "&::-webkit-scrollbar": {
        width: "5px",
        height: "5px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "rgb(181 154 79 / 58%)",
        borderRadius: "10px",
      },
    },
    [`& .${classes.deleteIconSize}`]: {
      width: "40px",
      height: "40px",
    },
    [`& .${classes.frameOptionsContainer}`]: {
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: "15px",
      padding: "0.5rem",
    },
    [`& .${classes.inputStyle}`]: {
      display: "flex",
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
      },
      justifyContent: "space-evenly",
      width: "100%",
      maxWidth: "460px",
      marginTop: "1rem",
      marginBottom: "1rem",
    },
    [`& .${classes.hdrModalContent}`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
    },
    [`& .${classes.filenameStyle}`]: {
      marginLeft: "5px",
      maxWidth: "200px",
    },
    [`& .${classes.fileButtonsStyle}`]: {
      display: "flex",
      flexDirection: "row",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
      maxWidth: "460px",
    },
    [`& .${classes.filenameContainer}`]: {
      marginTop: "1rem",
      width: "100%",
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
    [`& .${classes.hdrNameStyle}`]: {
      width: "100%",
    },
    [`& .${classes.titleStyle}`]: {
      marginTop: "0.5rem",
    },
    [`& .${classes.dividerStyle}`]: {
      height: "1px",
      width: "100%",
      marginTop: "1rem",
    },
  };
});

export default function HdrManagement({
  allHdr,
  setAllHdr,
  skybox,
  setSkybox,
}) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState(null);
  const [loader, setLoader] = useState(false);
  const [titleExist, setTitleExist] = useState("");

  function handleDelete(index) {
    myExpariencesService
      .deleteHdr(allHdr[index]._id)
      .then(() => {
        const tmp = [...allHdr];
        tmp.splice(index, 1);
        if (skybox === allHdr[index].url) {
          setSkybox(tmp[0].url);
        }
        setAllHdr(tmp);
        enqueueSnackbar(t("Crm.toasts.deleteSuccess"), { variant: "success" });
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("toasts.fetchCampaignContentError"), {
            variant: "error",
          });
      });
  }

  function handleFileChange(event) {
    if (event.target.files) {
      setFile([...event.target.files]);
    }
  }

  function sendFile() {
    if (!file || titleExist === "") {
      return;
    }

    const tmpFileObject = new File([file[0]], file[0]?.name, {
      type: file[0]?.type,
    });

    const tmp = [];
    tmp.push(tmpFileObject);

    setLoader(true);
    myExpariencesService
      .addHdr(tmp[0], titleExist)
      .then((res) => {
        const tmp = [...allHdr];

        setTitleExist("");
        setFile(null);
        tmp.push(res);
        setAllHdr(tmp);
        enqueueSnackbar(t("Crm.toasts.uploadSuccess"), { variant: "success" });
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else enqueueSnackbar(t("Crm.toasts.uploadFail"), { variant: "error" });
      })
      .finally(() => setLoader(false));
  }

  return (
    <StyledDiv>
      <div className={classes.hdrModalContent}>
        <Typography variant="h4" className={classes.titleStyle}>
          {t("Hdr.modalTitle")}
        </Typography>
        <div className={classes.fileButtonsStyle}>
          <div className={classes.filenameContainer}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<AddCircleOutlineOutlinedIcon />}
              size="large"
              id="add-hdr-button"
              color="primary"
              onChange={(e) => {
                handleFileChange(e);
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {t("Hdr.addHdr")}
              <input type="file" hidden />
            </Button>
            <Typography variant="caption" className={classes.filenameStyle}>
              {`${t("Hdr.filename")} ${file ? file[0].name : ""}`}
            </Typography>
          </div>
          <div className={classes.inputStyle}>
            <TextField
              variant="outlined"
              className={classes.hdrNameStyle}
              label={t("Hdr.hdrTitle")}
              value={titleExist ? titleExist : ""}
              onChange={(e) => {
                setTitleExist(e.target.value);
              }}
            />
          </div>
          <Button
            variant="outlined"
            size="large"
            color="primary"
            startIcon={loader ? null : <SendIcon />}
            disabled={
              (titleExist.length > 0 ? false : true) ||
              (file ? false : true) ||
              loader
            }
            onClick={() => sendFile()}
          >
            {loader ? <CircularProgress size={28} /> : t("Hdr.uploadHdr")}
          </Button>
          <Divider className={classes.dividerStyle} />
        </div>
        <Typography variant="h4" className={classes.titleStyle}>
          {t("Hdr.hdrList")}
        </Typography>
        <div className={classes.allHdrList}>
          {allHdr.map((item, index) => {
            return (
              <Paper
                elevation={24}
                className={classes.frameOptionsContainer}
                key={`hdr-list-${index + 1}`}
              >
                <Typography variant="body1">{item.name}</Typography>
                <IconButton
                  variant="outlined"
                  onClick={() => handleDelete(index)}
                  className={classes.deleteIconSize}
                >
                  <ClearIcon />
                </IconButton>
              </Paper>
            );
          })}
        </div>
      </div>
    </StyledDiv>
  );
}
